import { ArrowRight, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { CriticalCard as Card, CriticalCardContent as CardContent } from "@/components/layout/CriticalSurface";
import startersJson from "@/data/specimen-starters.json";
import { trackEvent } from "@/lib/analytics";

interface SpecimenStarterItem {
  clue: string;
  label: string;
  description: string;
  href: string;
}

const starters = startersJson as SpecimenStarterItem[];

const swatchClasses: Record<string, string> = {
  black_metallic: "bg-slate-800 ring-slate-500",
  green: "bg-emerald-600 ring-emerald-300",
  white_colorless: "bg-white ring-slate-300",
  red_rusty: "bg-red-700 ring-red-300",
  glassy: "bg-cyan-100 ring-cyan-400",
  banded_layered: "bg-gradient-to-r from-slate-700 via-stone-200 to-slate-700 ring-slate-400",
  vesicular: "bg-stone-500 ring-stone-300",
  scratches_glass: "bg-sky-200 ring-sky-400",
};

export function SpecimenStarter() {
  return (
    <Card className="overflow-hidden border-accent/30 bg-gradient-to-br from-card via-card to-accent/5">
      <CardContent className="p-5 md:p-7">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
              <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Start with what you can see
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold">What does your specimen look like?</h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Choose one obvious clue for a source-checked field guide. No account, photo, or technical vocabulary needed.
            </p>
          </div>
          <Link
            to="/quick-id"
            onClick={() => trackEvent("home_cta", { cta: "/quick-id", placement: "specimen_starter" })}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary underline-offset-4 hover:underline"
          >
            Use the full property key <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {starters.map((starter) => (
            <Link
              key={starter.clue}
              to={starter.href}
              onClick={() => trackEvent("home_specimen_start", {
                clue: starter.clue,
                destination: starter.href,
              })}
              className="group flex min-w-0 gap-3 rounded-xl border bg-background/80 p-3 transition-colors hover:border-accent/60 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <span className={`mt-1 h-4 w-4 shrink-0 rounded-full ring-2 ring-offset-2 ring-offset-background ${swatchClasses[starter.clue] ?? "bg-muted ring-border"}`} aria-hidden="true" />
              <span className="min-w-0">
                <span className="block font-semibold group-hover:text-accent">{starter.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{starter.description}</span>
              </span>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
